import { useEffect, useState } from "react";
import {
  Outlet,
  useNavigationType,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Aside from "./Aside";
import AdminTitle from "./AdminTitle";
import { BASE_URL } from "@/constants/constants";
import { isMobile } from "@/lib/dimmensions.utils";
import AsideMobile from "./AsideMobile";
import IconButton from "@/components/buttons/IconButton";
import { useUserStore } from "@/store/useUser";

const useFitMroToViewport = () => {
  useEffect(() => {
    const el = document.querySelector<HTMLElement>(".mro-fotos-componentes");
    if (!el) return;

    // Evita doble scroll: el contenedor host no scrollea, scrollea tu contenido interno
    el.style.overflow = "hidden";
    el.style.width = "100%";

    const apply = () => {
      const rect = el.getBoundingClientRect();
      const top = Math.max(0, rect.top);
      el.style.height = `calc(100dvh - ${top}px)`;
    };

    apply();
    window.addEventListener("resize", apply);

    // Por si cambia el layout superior (sticky header, banners, etc.)
    const ro = new ResizeObserver(apply);
    ro.observe(document.documentElement);

    return () => {
      window.removeEventListener("resize", apply);
      ro.disconnect();
    };
  }, []);
};

const AdminLayout = () => {
  useFitMroToViewport();
  const { isSupervisor } = useUserStore();
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (navigationType !== "PUSH") {
      if (!isSupervisor || isMobile()) {
        navigate(BASE_URL);
      } else if (
        pathname === `${BASE_URL}/admin` ||
        pathname === `${BASE_URL}/admin/` ||
        pathname.includes(`${BASE_URL}/admin/photos`)
      ) {
        navigate(`${BASE_URL}/admin/photos`);
      } else if (pathname.includes(`${BASE_URL}/admin/requests`)) {
        navigate(`${BASE_URL}/admin/requests`);
      } else {
        navigate(BASE_URL);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid="admin-layout"
      className="h-full w-full bg-neutral-100 relative"
    >
      <div className="h-full w-full flex">
        {/* ASIDE escritorio */}
        <Aside />
        {/* Columna principal */}
        <section className="flex-1 min-w-0 h-full flex flex-col bg-white">
          {/* Header (siempre) */}
          <header className="sticky top-0 z-40 bg-[#EEEEEE] ">
            <div className="h-12 px-4 flex items-center justify-between">
              <AdminTitle />
              <IconButton
                className="md:hidden"
                icon="mobileMenu"
                onClick={() => setMobileOpen(true)}
              />
            </div>
          </header>

          {/* CONTENIDO: este es el que scrollea */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="p-4 h-full">
              <Outlet />
            </div>
          </div>
        </section>

        {/* Menú móvil (overlay) */}
        {mobileOpen && (
          <div
            className="md:hidden fixed h-full w-full z-[60]"
            role="dialog"
            aria-modal="true"
          >
            {/* backdrop */}
            <button
              className="absolute inset-0 bg-black/40"
              aria-label="Cerrar menú"
              onClick={() => setMobileOpen(false)}
            />

            {/* panel */}
            <AsideMobile setMobileOpen={setMobileOpen} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLayout;
